/**
 * FILE-OVERVIEW:
 * - 役割: スケジュール編集とメモ出稿を行うモーダルUI。
 * - 主な連携: services/calendarApi.js, utils/newsmlBuilder.js, utils/eventHandlers.js
 * - 本番運用上の注意: フォーム初期値と出稿XMLパラメータの対応を崩さない。
 */

import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import { uploadData } from '@aws-amplify/storage';
import { buildArticleNewsML2CTS, buildPhotoNewsML2CTS, generateUniqueNoIdToken, toMemoIdSegment } from '../../utils/newsmlBuilder';
import { OUTPUT_BUCKET_NAME, S3_REGION } from '../../services/s3Service';
import { getCurrentUserInfo, getUserGroupsByUsername, listAssignableUsers } from '../../services/calendarApi';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from '@aws-amplify/auth';
import { 
  createDeleteHandler, 
  createSaveHandler 
} from '../../utils/eventHandlers';
import { createDuplicateHandler } from '../../utils/eventHelpers';
import { logInfo, logError } from '../../services/appLogger';

const MEMO_TYPE_DEFAULT_BY_GROUP = {
  system: 'シス用',
  design: '社会',
  Desks: '社会',
  group_society: '社会',
  branch_aomori: '地域',
  branch_tokyo: '地域',
  branch_towada: '地域',
  branch_misawa: '地域',
  branch_mutsu: '地域',
  branch_sannohe: '地域',
  branch_gonohe: '地域',
  branch_morioka: '地域',
  branch_kuji: '地域',
  branch_ninohe: '地域',
  'group_politics-economics': '経済',
  group_sports: '運動',
  group_photo: '写真',
  group_local: '地域',
  group_culture: '文化',
  group_digital: '地域',
  group_editorial: '天鐘',
  group_publication: '文化',
  Reporters: '社会',
  Users: 'ＮＥＣ',
};

const AVAILABLE_MEMO_TYPES = new Set([
  '経済',
  '社会',
  '政治',
  '地域',
  'ひろば',
  '運動',
  '文化',
  '天鐘',
  '自動組',
  'シス用',
  '写真',
  '線画',
  'ＥＰＳ',
  'ＰＤＦ',
  '大組',
  '箱組',
  'DB出',
  'ＮＥＣ',
]);

const AVAILABLE_SCHEDULE_CATEGORIES = new Set([
  '社会',
  '政治',
  '経済',
  '地域',
  '運動',
  '文化',
  '天鐘',
  'プライベート',
  '後日掲載',
]);

const MATERIAL_FILE_ACCEPT_BY_SLUG = {
  photo: 'image/jpeg,.jpg,.jpeg',
  eps: '.eps,application/postscript',
  pdf: '.pdf,application/pdf',
};

const PHOTO_MAX_FILE_SIZE = 10 * 1024 * 1024;

const toMaterialSlug = (kind) => {
  const raw = String(kind || '').trim();
  const normalized = raw.toUpperCase();
  if (raw === '記事') return 'article';
  if (raw === '写真' || raw === '画像') return 'photo';
  if (raw === 'ＥＰＳ' || normalized === 'EPS') return 'eps';
  if (raw === 'ＰＤＦ' || normalized === 'PDF') return 'pdf';
  return 'article';
};

const validateMaterialFile = (slug, file) => {
  if (!file || slug !== 'photo') return '';
  const mimeType = (file.type || '').toLowerCase();
  const isJpeg = mimeType ? mimeType === 'image/jpeg' : /\.jpe?g$/i.test(file.name || '');
  if (!isJpeg) {
    return '写真の添付ファイルはJPEG形式（.jpg / .jpeg）のみアップロードできます。';
  }
  if (file.size > PHOTO_MAX_FILE_SIZE) {
    return `写真の添付ファイルは10MBまでです（選択されたファイル: ${(file.size / (1024 * 1024)).toFixed(1)}MB）。`;
  }
  return '';
};

const normalizeMemoType = (value) => (AVAILABLE_MEMO_TYPES.has(value) ? value : '地域');
const normalizeScheduleCategory = (value) => (AVAILABLE_SCHEDULE_CATEGORIES.has(value) ? value : '地域');

const getDefaultMemoTypeForGroups = (groups) => {
  const normalizedGroups = Array.from(new Set((Array.isArray(groups) ? groups : [])
    .map((group) => String(group || '').trim())
    .filter(Boolean)));
  const lowerGroups = normalizedGroups.map((group) => group.toLowerCase());

  const priorityMatchers = [
    {
      match: () => lowerGroups.some((group) => group === 'system' || group === 'systems' || group.startsWith('system')),
      type: 'シス用',
    },
    {
      match: () => normalizedGroups.some((group) => group.toLowerCase().startsWith('branch_')),
      type: '地域',
    },
    {
      match: () => normalizedGroups.some((group) => group.toLowerCase().startsWith('group_')),
      type: null,
    },
    {
      match: () => lowerGroups.includes('design'),
      type: '社会',
    },
    {
      match: () => lowerGroups.includes('desks') || lowerGroups.includes('desk'),
      type: '社会',
    },
    {
      match: () => lowerGroups.includes('reporters') || lowerGroups.includes('reporter'),
      type: '社会',
    },
    {
      match: () => lowerGroups.includes('users') || lowerGroups.includes('user'),
      type: 'ＮＥＣ',
    },
  ];

  const matched = priorityMatchers.find((entry) => entry.match());
  if (!matched) return '地域';

  if (matched.type) return normalizeMemoType(matched.type);

  const groupTypeMap = {
    'group_politics-economics': '経済',
    group_sports: '運動',
    group_photo: '写真',
    group_local: '地域',
    group_culture: '文化',
    group_digital: '地域',
    group_editorial: '天鐘',
    group_publication: '文化',
    group_society: '社会',
  };
  const groupOrder = [
    'group_society',
    'group_politics-economics',
    'group_sports',
    'group_photo',
    'group_local',
    'group_culture',
    'group_digital',
    'group_editorial',
    'group_publication',
  ];

  const matchedGroup = groupOrder.find((groupKey) => lowerGroups.includes(groupKey));
  return matchedGroup ? normalizeMemoType(groupTypeMap[matchedGroup]) : '地域';
};

const getDefaultScheduleCategoryForGroups = (groups) => {
  const normalizedGroups = Array.from(new Set((Array.isArray(groups) ? groups : [])
    .map((group) => String(group || '').trim())
    .filter(Boolean)));
  const lowerGroups = normalizedGroups.map((group) => group.toLowerCase());

  const priorityMatchers = [
    {
      match: () => lowerGroups.some((group) => group === 'system' || group === 'systems' || group.startsWith('system')),
      type: '地域',
    },
    {
      match: () => lowerGroups.some((group) => group.startsWith('branch_')),
      type: '地域',
    },
    {
      match: () => lowerGroups.some((group) => group.startsWith('group_')),
      type: null,
    },
    {
      match: () => lowerGroups.includes('design'),
      type: '社会',
    },
    {
      match: () => lowerGroups.includes('desks') || lowerGroups.includes('desk'),
      type: '社会',
    },
    {
      match: () => lowerGroups.includes('reporters') || lowerGroups.includes('reporter'),
      type: '社会',
    },
    {
      match: () => lowerGroups.includes('users') || lowerGroups.includes('user'),
      type: '社会',
    },
  ];

  const matched = priorityMatchers.find((entry) => entry.match());
  if (!matched) return '地域';

  if (matched.type) return normalizeScheduleCategory(matched.type);

  const groupCategoryMap = {
    'group_politics-economics': '経済',
    group_sports: '運動',
    group_photo: '地域',
    group_local: '地域',
    group_culture: '文化',
    group_digital: '地域',
    group_editorial: '天鐘',
    group_publication: '文化',
    group_society: '社会',
  };
  const groupOrder = [
    'group_society',
    'group_politics-economics',
    'group_sports',
    'group_photo',
    'group_local',
    'group_culture',
    'group_digital',
    'group_editorial',
    'group_publication',
  ];

  const matchedGroup = groupOrder.find((groupKey) => lowerGroups.includes(groupKey));
  return matchedGroup ? normalizeScheduleCategory(groupCategoryMap[matchedGroup]) : '地域';
};
 
 
const EventDialog = ({ calendarState, calendarEvents, currentUser, isReadOnly = false }) => {
  const [mode, setMode] = useState('schedule');
  const isStandaloneMemoDialog = calendarState?.dialogMode === 'memo';
  const isMobile = false;
  const UNSET_TIME = '未定';
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [ownerGroups, setOwnerGroups] = useState([]);
  const EXCLUDED_ASSIGNABLE_USER_KEYWORDS = ['newsglobe', 'neckisya', 'necdesk'];

  const shouldExcludeAssignableUser = (user) => {
    const username = String(user?.username || '').toLowerCase();
    const name = String(user?.name || '').toLowerCase();
    return EXCLUDED_ASSIGNABLE_USER_KEYWORDS.some((keyword) => (
      username.includes(keyword) || name.includes(keyword)
    ));
  };

  const timeOptions = React.useMemo(() => {
    const options = [UNSET_TIME];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = String(hour).padStart(2, '0');
        const m = String(minute).padStart(2, '0');
        options.push(`${h}:${m}`);
      }
    }
    return options;
  }, []);

  const toDateValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const toTimeValue = (value, fallback = '00:00') => {
    if (!value) return fallback;
    if (typeof value === 'string') {
      const m = value.match(/T(\d{2}:\d{2})/);
      if (m) return m[1];
      if (/^\d{2}:\d{2}$/.test(value)) return value;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${min}`;
  };

  const buildDateTimeValue = (dateValue, timeValue) => {
    const date = toDateValue(dateValue);
    if (!date) return '';
    if (!timeValue || timeValue === UNSET_TIME) return date;
    return `${date}T${timeValue}`;
  };

  const renderTimeSelect = (fieldId, fieldName, fieldLabel, fieldValue, onValueChange) => {
    // 15分刻みに無い既存データ（例: 10:03）でも選択肢に含めて表示できるようにする
    const selectOptions = timeOptions.includes(fieldValue)
      ? timeOptions
      : [fieldValue, ...timeOptions];
    return (
      <TextField
        margin="dense"
        id={fieldId}
        name={fieldName}
        label={fieldLabel}
        select
        fullWidth
        variant="outlined"
        size="small"
        value={fieldValue}
        onChange={onValueChange}
        InputLabelProps={{ shrink: true }}
        autoComplete="off"
      >
        {selectOptions.map((time) => (
          <MenuItem key={time} value={time}>
            {time}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const hasId = !!(calendarState && calendarState.tempEvent && (calendarState.tempEvent.id || calendarState.tempEvent._id));
  const isPrivateCategory = (calendarState?.tempEvent?.category || '') === 'プライベート';

  const fallbackAssignableUsers = React.useMemo(() => {
    const map = new Map();
    const events = Array.isArray(calendarEvents?.events) ? calendarEvents.events : [];

    events.forEach((ev) => {
      const userId = (ev?.createdBy || '').trim();
      if (!userId) return;
      if (!map.has(userId)) {
        map.set(userId, {
          userId,
          username: (ev?.createdByUsername || '').trim(),
          name: (ev?.person || '').trim(),
          email: '',
        });
      }
    });

    const selfUserId = (currentUser?.userId || '').trim();
    if (selfUserId && !map.has(selfUserId)) {
      const selfName = (currentUser?.name || '').trim();
      map.set(selfUserId, {
        userId: selfUserId,
        username: (currentUser?.username || '').trim(),
        name: selfName,
        email: currentUser?.email || '',
      });
    }

    return Array.from(map.values()).filter((user) => !shouldExcludeAssignableUser(user));
  }, [calendarEvents?.events, currentUser?.userId, currentUser?.username, currentUser?.name, currentUser?.email]);

  const ownerOptions = React.useMemo(() => {
    const base = Array.isArray(assignableUsers)
      ? assignableUsers.filter((user) => !shouldExcludeAssignableUser(user))
      : [];
    const currentOwnerId = calendarState?.tempEvent?.createdBy || '';
    const currentOwnerUsername = (calendarState?.tempEvent?.createdByUsername || '').trim();
    if (currentOwnerId && !base.some((u) => u.userId === currentOwnerId)) {
      base.unshift({
        userId: currentOwnerId,
        username: currentOwnerUsername,
        // 候補外ユーザーが現在オーナーの場合でも、古い person 名ではなく username を表示する
        name: currentOwnerUsername || '名称未解決',
        displayName: currentOwnerUsername || '名称未解決',
        email: '',
      });
    }
    return base;
  }, [assignableUsers, calendarState?.tempEvent?.createdBy, calendarState?.tempEvent?.createdByUsername]);

  // 日付フォーマット YYYY-MM-DD
  const formatDateDash = (d) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const da = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  };
  const defaultTitle = (calendarState?.tempEvent?.title || '').trim();
  const todayDash = formatDateDash(new Date());
  const defaultCategory = '';
  const defaultScheduleCategory = React.useMemo(
    () => getDefaultScheduleCategoryForGroups(ownerGroups),
    [ownerGroups]
  );
  const defaultMemoType = React.useMemo(
    () => getDefaultMemoTypeForGroups(ownerGroups),
    [ownerGroups]
  );
  const memoTypeTouchedRef = React.useRef(false);

  const [themeName, setThemeName] = useState(defaultTitle);
  const [memoThemeContent, setMemoThemeContent] = useState('');
  const [memoType, setMemoType] = useState(defaultMemoType);
  const [memoPriority, setMemoPriority] = useState('ベタ');
  const [memoRegistrationDate, setMemoRegistrationDate] = useState(todayDash);
  const [memoCategory, setMemoCategory] = useState(defaultCategory);

  const [materials, setMaterials] = useState([
    { kind: '記事', headline: defaultTitle, caption: '', lines: '', regDate: todayDash, note: '', articleBody: '', category: defaultCategory, linkUrl: '', file: null }
  ]);

  const updateMaterial = (idx, key, value) => {
    setMaterials((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  const addMaterial = () => {
    setMaterials((prev) => ([
      ...prev,
      { kind: '記事', headline: (themeName || defaultTitle), caption: '', lines: '', regDate: memoRegistrationDate || todayDash, note: '', articleBody: '', category: memoCategory || defaultCategory, linkUrl: '', file: null }
    ]));
  };

  const sanitizeLineCount = (value) => {
    const raw = value == null ? '' : String(value).trim();
    if (!raw) return '';
    if (!/^\d+$/.test(raw)) return '';
    return raw.replace(/^0+(?=\d)/, '');
  };

  const removeMaterial = (idx) => {
    setMaterials((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDelete = createDeleteHandler(calendarState, calendarEvents, currentUser);
  const handleDuplicate = createDuplicateHandler(calendarState);
  const handleSave = createSaveHandler(calendarState, calendarEvents, currentUser);
  const currentOwnerUsername = (calendarState?.tempEvent?.createdByUsername || '').trim();
  const currentOwnerId = (calendarState?.tempEvent?.createdBy || '').trim();

  useEffect(() => {
    let mounted = true;

    const resolveOwnerGroups = async () => {
      const matchedOwner = ownerOptions.find((user) => user.userId === currentOwnerId) || null;
      const groupsFromOption = Array.isArray(matchedOwner?.groups) ? matchedOwner.groups.filter(Boolean) : [];
      if (groupsFromOption.length > 0) {
        if (mounted) setOwnerGroups(groupsFromOption);
        return;
      }

      const ownerUsername = String(matchedOwner?.username || currentOwnerUsername || '').trim();
      if (!ownerUsername) {
        if (mounted) setOwnerGroups([]);
        return;
      }

      try {
        const groups = await getUserGroupsByUsername(ownerUsername);
        if (mounted) setOwnerGroups(Array.isArray(groups) ? groups : []);
      } catch (error) {
        if (mounted) setOwnerGroups([]);
      }
    };

    resolveOwnerGroups();

    return () => {
      mounted = false;
    };
  }, [currentOwnerId, currentOwnerUsername, ownerOptions]);

  useEffect(() => {
    if (!calendarState?.open || mode !== 'schedule' || !calendarState?.tempEvent) return;
    const currentCategory = String(calendarState.tempEvent.category || '').trim();
    if (currentCategory) return;

    calendarState.setTempEvent((prev) => {
      const prevCategory = String(prev?.category || '').trim();
      if (prevCategory) return prev;
      return {
        ...prev,
        category: defaultScheduleCategory,
      };
    });
  }, [calendarState, defaultScheduleCategory, mode]);

  const openMemoMode = () => {
    if (isReadOnly) {
      alert('このユーザー権限ではメモ出稿できません（閲覧のみ）。');
      return;
    }

    const ev = calendarState?.tempEvent || {};
    const titleFromSchedule = (ev.title || '').trim();
    const categoryFromSchedule = ev.category || defaultCategory;
    const regDateDefault = todayDash;

    // テーマ＆メモ既定をスケジュール値で初期化
    setThemeName(titleFromSchedule);
    setMemoThemeContent('');
    setMemoPriority('ベタ');
    setMemoRegistrationDate(regDateDefault);
    // 種別のデフォルトはスケジュール所有者の所属グループから決定
    setMemoType(defaultMemoType);
    memoTypeTouchedRef.current = false;
    // 分類のデフォルトは空（既存挙動を維持）
    setMemoCategory('');
    setMaterials([
      { kind: '記事', headline: titleFromSchedule || defaultTitle, lines: '', regDate: regDateDefault, note: '', articleBody: '', category: '', linkUrl: '', file: null }
    ]);
    setMode('memo');
  };
  // ヘルパー: YYYYMMDD 文字列
  const formatDateYYYYMMDD = (d) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const da = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${da}`;
  };

  // XML宣言のencodingをUTF-16BEに統一
  const ensureUtf16EncodingHeader = (xml) => {
    if (!xml) return `<?xml version="1.0" encoding="UTF-16BE"?>\n`;
    return xml.replace(/<\?xml[^>]*encoding=["']([^"']+)["'][^>]*\?>/i, '<?xml version="1.0" encoding="UTF-16BE"?>');
  };

  // 文字列→UTF-16BE(BOM付き)のUint8Arrayへ
  const toUtf16BEUint8Array = (str) => {
    const buf = new Uint8Array((str.length + 1) * 2);
    // BOM FE FF
    buf[0] = 0xFE; buf[1] = 0xFF;
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      buf[2 + i * 2] = (code >> 8) & 0xFF; // high byte
      buf[3 + i * 2] = code & 0xFF;        // low byte
    }
    return buf;
  };

  const publishMemoListXml = async () => {
    if (isReadOnly) {
      alert('このユーザー権限ではメモ出稿できません（閲覧のみ）。');
      return false;
    }

    try {
      const ev = calendarState.tempEvent || {};
      if ((ev.category || '') === 'プライベート') {
        alert('プライベートに設定されたスケジュールはメモ出稿できません。');
        return false;
      }
      const eventId = ev.id || ev._id || 'noid';
      const exportScheduleId = eventId === 'noid' ? generateUniqueNoIdToken() : String(eventId);
      const shortScheduleId = toMemoIdSegment(exportScheduleId);
      const reporterName = (ev.person || '').trim() || '未設定';
      const themeNameLocal = (themeName || ev.title || '').trim();
      const memoThemeContentLocal = (memoThemeContent || ev.notes || '').trim();
      const effectiveMemoPriority = memoPriority || 'ベタ';
      const selectedMemoType = String(memoType ?? '').replace(/\u3000/g, ' ').trim() || 'ＮＥＣ';
      const outputDateStr = formatDateYYYYMMDD(new Date());

      const userInfo = await getCurrentUserInfo().catch(() => ({ username: 'unknown' }));
      const username = userInfo?.username || 'unknown';

      const results = [];
      const templateBySlug = {
        article: 'NewsML2CTS_ul_article.xml',
        photo: 'NewsML2CTS_ul_photo.xml',
        eps: 'NewsML2CTS_ul_eps.xml',
        pdf: 'NewsML2CTS_ul_pdf.xml',
      };
      const kindToSlug = (kind) => {
        const raw = kind == null ? '' : String(kind).trim();
        const lower = raw.toLowerCase();
        if (raw === '記事') return 'article';
        if (raw === '写真' || raw === '画像') return 'photo';
        if (raw === 'ＥＰＳ' || lower === 'eps') return 'eps';
        if (raw === 'ＰＤＦ' || lower === 'pdf') return 'pdf';
        if (raw === 'エクセル' || lower === 'excel') return 'excel';
        if (raw === '動画' || lower === 'video') return 'video';
        return lower || 'article';
      };
      // テーマ内の素材種別ごとに連番カウント
      let counters = { article: 0, photo: 0, eps: 0, pdf: 0, excel: 0, video: 0 };

      for (const m of materials) {
        const kind = m.kind || '記事';
        const normalizedKind = (kind === '画像') ? '写真' : kind;
        const typeSlug = kindToSlug(normalizedKind);
        counters[typeSlug] = (counters[typeSlug] || 0) + 1;
        const serialNumber = counters[typeSlug];
        const serial = String(serialNumber).padStart(4, '0');
        const headline = m.headline || '';
        const materialRegDate = m.regDate || ev.start || new Date();
        // ファイル名の先頭日付は「スケジュールのカレンダー上の日付(yyyymmdd)」
        const calendarDateStr = formatDateYYYYMMDD(ev.start || new Date());
        // スケジュール登録日時（yyyymmdd）
        const createdAtDate = ev.createdAt ? new Date(ev.createdAt) : new Date();
        const createdDateStr = formatDateYYYYMMDD(createdAtDate);
        const templateName = (() => {
          if (typeSlug === 'article') return templateBySlug.article;
          if (typeSlug === 'photo') return templateBySlug.photo;
          if (typeSlug === 'eps') return templateBySlug.eps;
          if (typeSlug === 'pdf') return templateBySlug.pdf;
          return null;
        })();

        // 仕様: yyyymmdd_yyyymmdd_memo_<スケジュールID>-<素材種別>_nnnn.xml
        // (カレンダー上の日付)_(登録日時)_memo...
        const baseFileName = `${calendarDateStr}_${createdDateStr}_memo_${shortScheduleId}-${typeSlug}_${serial}`;
        const filename = `${baseFileName}.xml`;
        // S3出力先: 記事は article/file/tmp、写真/EPS/PDF は photo/file/tmp
        const folderPath = (typeSlug === 'article') ? 'article/file/tmp' : 'photo/file/tmp';
        // publicプレフィックスを避けるため、keyに直接パスを指定
        const key = `${folderPath}/${filename}`;

        if (!templateName) {
          console.warn('未対応の素材種別のため出稿をスキップします。', { kind: normalizedKind, typeSlug });
          continue;
        }

        let xml = '';
        switch (templateName) {
          case 'NewsML2CTS_ul_article.xml':
            xml = buildArticleNewsML2CTS({
              themeName: themeNameLocal,
              themeContent: memoThemeContentLocal,
              memoPriority: effectiveMemoPriority,
              memoRegistrationDate: typeof materialRegDate === 'string' ? materialRegDate : `${materialRegDate.getFullYear()}-${String(materialRegDate.getMonth()+1).padStart(2,'0')}-${String(materialRegDate.getDate()).padStart(2,'0')}`,
              reporterName,
              eventStart: ev.start || new Date(),
              username,
              materialHeadline: headline,
              materialLines: sanitizeLineCount(m.lines),
              articleBody: m.articleBody || '',
              scheduleId: exportScheduleId,
              materialSerial: serialNumber,
              memoCategory: m.category || memoCategory,
              memoType: selectedMemoType,
            });
            break;
          case 'NewsML2CTS_ul_photo.xml':
          case 'NewsML2CTS_ul_eps.xml':
          case 'NewsML2CTS_ul_pdf.xml': {
            const photoMaterialKind = typeSlug === 'eps'
              ? 'ＥＰＳ'
              : (typeSlug === 'pdf' ? 'ＰＤＦ' : '写真');
            const entityExtension = (() => {
              if (m.file && m.file.name) {
                const match = /\.[^.]+$/.exec(m.file.name || '');
                if (match) return match[0].toLowerCase();
              }
              return typeSlug === 'eps' ? '.eps' : typeSlug === 'pdf' ? '.pdf' : '.jpg';
            })();
            xml = buildPhotoNewsML2CTS({
              themeName: themeNameLocal,
              themeContent: memoThemeContentLocal,
              memoPriority: effectiveMemoPriority,
              memoRegistrationDate: typeof materialRegDate === 'string' ? materialRegDate : `${materialRegDate.getFullYear()}-${String(materialRegDate.getMonth()+1).padStart(2,'0')}-${String(materialRegDate.getDate()).padStart(2,'0')}`,
              reporterName,
              eventStart: ev.start || new Date(),
              username,
              materialHeadline: headline,
              caption: m.caption ?? '',
              materialKind: photoMaterialKind,
              scheduleId: exportScheduleId,
              materialSerial: serialNumber,
              memoCategory: m.category || memoCategory,
              memoType: selectedMemoType,
              entityFileName: `${baseFileName}${entityExtension}`,
            });
            break;
          }
          default:
            continue;
        }

        // ヘッダーencodingをUTF-16BEに修正 → バイト化
        const xmlUtf16Header = ensureUtf16EncodingHeader(xml);
        const xmlBytes = toUtf16BEUint8Array(xmlUtf16Header);

        console.log('アップロード開始:', {
          templateName,
          key,
          bucketName: OUTPUT_BUCKET_NAME,
          fileSize: xmlBytes.length,
        });
        
        // AWS SDK S3クライアントを使用して直接アップロード
        const session = await fetchAuthSession();
        const s3Client = new S3Client({
          region: S3_REGION,
          credentials: session.credentials,
        });
        
        const command = new PutObjectCommand({
          Bucket: OUTPUT_BUCKET_NAME,
          Key: key,
          Body: xmlBytes,
          ContentType: 'application/xml;charset=UTF-16BE',
        });
        
        const uploadResult = await s3Client.send(command);
        
        console.log('アップロード成功:', uploadResult);

        if (typeSlug === 'photo' && m.file) {
          const extMatch = /\.[^.]+$/.exec(m.file.name || '');
          const fileExt = extMatch ? extMatch[0].toLowerCase() : '.jpg';
          const attachmentKey = `${folderPath}/${baseFileName}${fileExt}`;
          const attachmentCommand = new PutObjectCommand({
            Bucket: OUTPUT_BUCKET_NAME,
            Key: attachmentKey,
            Body: await m.file.arrayBuffer().then((buffer) => new Uint8Array(buffer)),
            ContentType: m.file.type || 'application/octet-stream',
          });
          const attachmentUploadResult = await s3Client.send(attachmentCommand);
          console.log('添付画像アップロード成功:', {
            attachmentKey,
            attachmentUploadResult,
          });
        }

        results.push(key);
      }

      setMode('schedule');
      // 出稿完了後、メモ項目をクリア
      setThemeName('');
      setMemoThemeContent('');
      setMemoPriority('ベタ');
      setMemoRegistrationDate('');
      setMemoCategory('');
      setMaterials([
        { kind: '記事', headline: '', lines: '', regDate: '', note: '', articleBody: '', category: '', linkUrl: '', file: null }
      ]);
      const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
      await logInfo(
        'memo_publish_success',
        {
          scheduleId: String(eventId),
          memoTheme: themeNameLocal || '(テーマなし)',
          publishedCount: results.length,
          keys: results,
        },
        `${actor}がメモ「${themeNameLocal || '(テーマなし)'}」を出稿しました。`
      );
      alert(`NewsML形式のメモXMLをS3に保存しました\n${results.map((k)=>`- ${k}`).join('\n')}`);
      return true;
    } catch (error) {
      const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
      await logError(
        'memo_publish_failed',
        {
          scheduleId: String(calendarState?.tempEvent?.id || ''),
          memoTheme: (themeName || calendarState?.tempEvent?.title || '').trim() || '(テーマなし)',
          reason: error?.message || 'unknown_error',
        },
        `${actor}のメモ出稿に失敗しました。`
      );
      console.error('メモ一覧XML作成/保存エラー:', error);
      console.error('エラー詳細:', {
        message: error?.message,
        code: error?.code,
        statusCode: error?.statusCode,
        response: error?.response,
      });
      alert(`メモ一覧XMLの保存中にエラーが発生しました。\n詳細: ${error?.message || '不明なエラー'}`);
      return false;
    }
  };

  const closeStandaloneMemoDialog = () => {
    calendarState.setOpen(false);
    calendarState.setDialogMode?.('schedule');
  };

  const handleStandalonePublish = async () => {
    const isSuccess = await publishMemoListXml();
    if (isSuccess) {
      closeStandaloneMemoDialog();
    }
  };

  // メモモードに入った際にデフォルトを充当（空のもののみ）
  useEffect(() => {
    if (mode === 'memo') {
      const effectiveThemeName = (themeName || defaultTitle);
      setThemeName((prev) => prev || defaultTitle);
      setMemoRegistrationDate((prev) => prev || todayDash);
      if (!memoTypeTouchedRef.current) {
        setMemoType(defaultMemoType);
      }
      setMemoCategory((prev) => prev || defaultCategory);
      setMaterials((prev) => prev.map((m) => ({
        ...m,
        regDate: m.regDate || todayDash,
        category: m.category || memoCategory,
        headline: m.headline || effectiveThemeName,
      })));
    }
  }, [mode, defaultMemoType, defaultTitle, memoCategory, themeName, todayDash]);

  // ダイアログを開くたびにスケジュールモードへ初期化
  useEffect(() => {
    if (calendarState?.open) {
      if (calendarState?.dialogMode === 'memo' && !isReadOnly) {
        const ev = calendarState?.tempEvent || {};
        const titleFromSchedule = (ev.title || '').trim();
        const regDateDefault = todayDash;

        setThemeName(titleFromSchedule);
        setMemoThemeContent('');
        setMemoPriority('ベタ');
        setMemoRegistrationDate(regDateDefault);
        setMemoType(defaultMemoType);
        memoTypeTouchedRef.current = false;
        setMemoCategory('');
        setMaterials([
          { kind: '記事', headline: titleFromSchedule || defaultTitle, lines: '', regDate: regDateDefault, note: '', articleBody: '', category: '', linkUrl: '', file: null }
        ]);
        setMode('memo');
      } else {
        setMode('schedule');
      }
    }
  }, [calendarState?.open, calendarState?.dialogMode, defaultMemoType, defaultTitle, isReadOnly, todayDash, calendarState?.tempEvent]);

  useEffect(() => {
    let mounted = true;
    if (!calendarState?.open || mode !== 'schedule' || !hasId) return () => { mounted = false; };

    (async () => {
      setIsLoadingUsers(true);
      try {
        const users = await listAssignableUsers();
        if (!mounted) return;

        // ここではすでに listAssignableUsers が name 解決済みのため、そのまま使う
        const completedUsers = (Array.isArray(users) ? users : [])
          .map((u) => ({
            ...u,
            userId: (u?.userId || '').trim(),
            username: (u?.username || '').trim(),
            name: (u?.name || '').trim(),
            displayName: (u?.displayName || '').trim(),
          }))
          .filter((u) => u.userId && u.username);

        setAssignableUsers(completedUsers);
      } finally {
        if (mounted) setIsLoadingUsers(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [calendarState?.open, mode, hasId]);

  return (
    <Dialog
      open={calendarState.open}
      onClose={() => calendarState.setOpen(false)}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      fullWidth
      maxWidth="sm"
    >
      <IconButton
        onClick={() => calendarState.setOpen(false)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: (theme) => theme.palette.grey[700],
          zIndex: 1301,
          touchAction: 'manipulation',
        }}
      >
        <CancelIcon sx={{ fontSize: 32 }} />
      </IconButton>

      <DialogContent
        sx={{
          '& .MuiInputBase-input': { fontSize: '0.875rem' },
          '& .MuiInputLabel-root': { fontSize: '0.875rem' },
          '& .MuiFormLabel-root': { fontSize: '0.875rem' },
          '& .MuiSelect-select': { fontSize: '0.875rem' },

          // 追加: 入力／セレクトのパディングを少し減らす
          '& .MuiOutlinedInput-input': { padding: '6px 10px' },
          '& .MuiOutlinedInput-inputMultiline, & .MuiInputBase-inputMultiline': { padding: '6px 10px' },
          '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingLeft: '10px', // 右側はアイコン用スペースを保持
          },

          // 特定クラス(.css-azjfyu)が付与されたフォームアイテムはパディングを0に
          '& .css-azjfyu .MuiOutlinedInput-input, & .css-azjfyu .MuiOutlinedInput-inputMultiline, & .css-azjfyu .MuiInputBase-inputMultiline': {
            padding: 0,
          },

          '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
        }}
      >
        <Typography
          variant={isMobile ? 'subtitle2' : 'subtitle1'}
          gutterBottom
          sx={isMobile ? { fontSize: '0.875rem' } : {}}
        >
          {mode === 'schedule'
            ? (isReadOnly ? '取材スケジュール閲覧' : '取材スケジュールを追加/編集')
            : (isStandaloneMemoDialog ? '記事・画像出稿（メモ一覧）' : 'メモ出稿')}
        </Typography>

        {mode === 'schedule' && (
          <Box component="fieldset" disabled={isReadOnly} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
            <TextField
              margin="dense"
              id="person"
              name="person"
              label="記者名"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={calendarState.tempEvent?.person || ''}
              onChange={(e) => calendarState.setTempEvent({
                ...calendarState.tempEvent,
                person: e.target.value
              })}
              autoComplete="name"
            />

            {hasId && (
              <Autocomplete
                fullWidth
                size="small"
                options={ownerOptions}
                loading={isLoadingUsers}
                value={
                  isLoadingUsers
                    ? { userId: calendarState.tempEvent?.createdBy || '', displayName: 'Loading...' }
                    : ownerOptions.find((u) => u.userId === (calendarState.tempEvent?.createdBy || '')) || null
                }
                isOptionEqualToValue={(option, value) => option?.userId === value?.userId}
                getOptionLabel={(option) => option?.displayName || option?.name || option?.username || ''}
                noOptionsText="ユーザーを取得できません"
                loadingText="読み込み中..."
                onChange={(_, selectedUser) => {
                  calendarState.setTempEvent({
                    ...calendarState.tempEvent,
                    createdBy: selectedUser?.userId || '',
                    createdByUsername: selectedUser?.username || '',
                    person: selectedUser?.name || calendarState.tempEvent?.person || ''
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    id="owner-user"
                    name="owner-user"
                    label="移動先ユーザー"
                    autoComplete="off"
                  />
                )}
              />
            )}

            <RadioGroup
              id="area"
              name="area"
              value={calendarState.tempEvent?.area || '本社'}
              onChange={(e) => calendarState.setTempEvent({
                ...calendarState.tempEvent,
                area: e.target.value
              })}
              row
              sx={{ mb: 1 }}
            >
              <FormControlLabel value="本社" control={<Radio />} label="本社" />
              <FormControlLabel value="青森" control={<Radio />} label="青森" />
              <FormControlLabel value="総支局" control={<Radio />} label="総支局" />
            </RadioGroup>

            <RadioGroup
              id="category"
              name="category"
              value={calendarState.tempEvent?.category || defaultScheduleCategory}
              onChange={(e) => calendarState.setTempEvent({
                ...calendarState.tempEvent,
                category: e.target.value
              })}
              row
              sx={{ mb: 1 }}
            >
              <FormControlLabel value="社会" control={<Radio />} label="社会" />
              <FormControlLabel value="政治" control={<Radio />} label="政治" />
              <FormControlLabel value="経済" control={<Radio />} label="経済" />
              <FormControlLabel value="地域" control={<Radio />} label="地域" />
              <FormControlLabel value="運動" control={<Radio />} label="運動" />
              <FormControlLabel value="文化" control={<Radio />} label="文化" />
              <FormControlLabel value="天鐘" control={<Radio />} label="天鐘" />
              <FormControlLabel value="プライベート" control={<Radio />} label="プライベート" />
              <FormControlLabel value="後日掲載" control={<Radio />} label="後日掲載" />
            </RadioGroup>

            <TextField
              margin="dense"
              name="title"
              id="title"
              label="タイトル"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={calendarState.tempEvent?.title || ''}
              onChange={(e) => {
                calendarState.setTempEvent({
                  ...calendarState.tempEvent,
                  title: e.target.value
                });
                setThemeName(e.target.value);
              }}
              autoComplete="off"
            />

            <TextField
              margin="dense"
              id="notes"
              name="notes"
              label="備考"
              type="text"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              size="small"
              value={calendarState.tempEvent?.notes ?? ''}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => calendarState.setTempEvent({
                ...calendarState.tempEvent,
                notes: e.target.value
              })}
              autoComplete="off"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField
                margin="dense"
                id="start-date"
                name="start-date"
                label="開始日"
                type="date"
                fullWidth
                variant="outlined"
                size="small"
                value={toDateValue(calendarState.tempEvent?.start)}
                onChange={(e) => calendarState.setTempEvent({
                  ...calendarState.tempEvent,
                  start: buildDateTimeValue(e.target.value, calendarState.tempEvent?.startTime || UNSET_TIME)
                })}
                InputLabelProps={{ shrink: true }}
                autoComplete="off"
              />

              {renderTimeSelect(
                'start-time',
                'start-time',
                '開始時刻',
                calendarState.tempEvent?.startTime || UNSET_TIME,
                (e) => calendarState.setTempEvent({
                  ...calendarState.tempEvent,
                  startTime: e.target.value,
                  start: buildDateTimeValue(calendarState.tempEvent?.start, e.target.value)
                })
              )}

              <TextField
                margin="dense"
                id="end-date"
                name="end-date"
                label="終了日"
                type="date"
                fullWidth
                variant="outlined"
                size="small"
                value={toDateValue(calendarState.tempEvent?.end || calendarState.tempEvent?.start)}
                onChange={(e) => calendarState.setTempEvent({
                  ...calendarState.tempEvent,
                  end: buildDateTimeValue(e.target.value, calendarState.tempEvent?.endTime || UNSET_TIME)
                })}
                InputLabelProps={{ shrink: true }}
                autoComplete="off"
              />

              {renderTimeSelect(
                'end-time',
                'end-time',
                '終了時刻',
                calendarState.tempEvent?.endTime || UNSET_TIME,
                (e) => calendarState.setTempEvent({
                  ...calendarState.tempEvent,
                  endTime: e.target.value,
                  end: buildDateTimeValue(calendarState.tempEvent?.end || calendarState.tempEvent?.start, e.target.value)
                })
              )}
            </Box>
          </Box>
        )}

        {mode === 'memo' && (
          <Box component="fieldset" disabled={isReadOnly} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
            {!isStandaloneMemoDialog && (
              <>
                <Typography
                  variant={isMobile ? 'subtitle2' : 'subtitle1'}
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  テーマ
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <TextField
                    label="テーマ名"
                    fullWidth
                    value={themeName}
                    onChange={(e) => setThemeName(e.target.value)}
                  />
                </Box>

                {/* 追加: テーマの内容 / 種別 / 重要度 / 登録予定日 / 分類 */}
                <Box sx={{ mb: 1, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  <Box sx={{ mb: 1, gridColumn: '1 / -1' }}>
                    <TextField
                      label="テーマの内容"
                      fullWidth
                      multiline
                      minRows={1}
                      size="small"
                      margin="dense"
                      variant="outlined"
                      value={memoThemeContent ?? ''}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setMemoThemeContent(e.target.value)}
                    />
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>種別</InputLabel>
                      <Select
                        label="種別"
                        value={memoType}
                        onChange={(e) => {
                          memoTypeTouchedRef.current = true;
                          setMemoType(e.target.value);
                        }}
                        displayEmpty
                        renderValue={(v) => (v ? v : '')}
                      >
                        <MenuItem value="　">　</MenuItem>
                        <MenuItem value="経済">経済</MenuItem>
                        <MenuItem value="社会">社会</MenuItem>
                        <MenuItem value="政治">政治</MenuItem>
                        <MenuItem value="地域">地域</MenuItem>
                        <MenuItem value="ひろば">ひろば</MenuItem>
                        <MenuItem value="運動">運動</MenuItem>
                        <MenuItem value="文化">文化</MenuItem>
                        <MenuItem value="天鐘">天鐘</MenuItem>
                        <MenuItem value="自動組">自動組</MenuItem>
                        <MenuItem value="シス用">シス用</MenuItem>
                        <MenuItem value="写真">写真</MenuItem>
                        <MenuItem value="線画">線画</MenuItem>
                        <MenuItem value="ＥＰＳ">ＥＰＳ</MenuItem>
                        <MenuItem value="ＰＤＦ">ＰＤＦ</MenuItem>
                        <MenuItem value="大組">大組</MenuItem>
                        <MenuItem value="箱組">箱組</MenuItem>
                        <MenuItem value="DB出">DB出</MenuItem>
                        <MenuItem value="ＮＥＣ">ＮＥＣ</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ mb: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>分類</InputLabel>
                      <Select
                        label="分類"
                        value={memoCategory}
                        onChange={(e) => setMemoCategory(e.target.value)}
                        displayEmpty
                        renderValue={(v) => (v ? v : '')}
                      >
                        <MenuItem value="　">　</MenuItem>
                        <MenuItem value="文化">文化</MenuItem>
                        <MenuItem value="事件・司法">事件・司法</MenuItem>
                        <MenuItem value="災害・事故">災害・事故</MenuItem>
                        <MenuItem value="経済・産業">経済・産業</MenuItem>
                        <MenuItem value="教育">教育</MenuItem>
                        <MenuItem value="環境問題">環境問題</MenuItem>
                        <MenuItem value="保険衛生">保険衛生</MenuItem>
                        <MenuItem value="人間的興味">人間的興味</MenuItem>
                        <MenuItem value="労働問題">労働問題</MenuItem>
                        <MenuItem value="ライフスタイル・レジャー">ライフスタイル・レジャー</MenuItem>
                        <MenuItem value="政治">政治</MenuItem>
                        <MenuItem value="宗教・思想">宗教・思想</MenuItem>
                        <MenuItem value="科学・テクノロジー">科学・テクノロジー</MenuItem>
                        <MenuItem value="社会問題">社会問題</MenuItem>
                        <MenuItem value="スポーツ">スポーツ</MenuItem>
                        <MenuItem value="社会不安・紛争・戦争">社会不安・紛争・戦争</MenuItem>
                        <MenuItem value="気象">気象</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <TextField
                      label="登録予定日"
                      type="date"
                      fullWidth
                      size="small"
                      value={memoRegistrationDate}
                      onChange={(e) => setMemoRegistrationDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>重要度</InputLabel>
                      <Select
                        label="重要度"
                        value={memoPriority}
                        onChange={(e) => setMemoPriority(e.target.value)}
                      >
                        <MenuItem value="ベタ">ベタ</MenuItem>
                        <MenuItem value="トップ">トップ</MenuItem>
                        <MenuItem value="段もの">段もの</MenuItem>
                        <MenuItem value="取り置き可">取り置き可</MenuItem>
                        <MenuItem value="その他">その他</MenuItem>
                        <MenuItem value="決まりもの">決まりもの</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 1 }} />
              </>
            )}
            <Typography
              variant={isMobile ? 'subtitle2' : 'subtitle1'}
              sx={{ fontWeight: 600, mb: 1 }}
            >
              メモ一覧
            </Typography>
            {materials.map((m, idx) => (
              <Box key={idx} sx={{ mb: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {(() => {
                  const materialSlug = toMaterialSlug(m.kind);
                  const showAttachSection = materialSlug !== 'article';
                  return (
                    <>
                {/* ヘッダー: 素材番号・種別・削除 */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ fontWeight: 600 }}>
                    素材 {idx + 1} ・ {m.kind}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeMaterial(idx)}
                    disabled={materials.length === 1}
                    aria-label="remove-material"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>

                {/* 1行目：素材種別／登録予定日／行数 */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '220px 1fr 140px' },
                    gap: 1,
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box>
                    <FormControl fullWidth size="small">
                      <InputLabel>素材種別</InputLabel>
                      <Select
                        label="素材種別"
                        value={m.kind}
                        onChange={(e) => {
                          const selectedKind = e.target.value;
                          const next = { kind: selectedKind };
                          if (toMaterialSlug(selectedKind) === 'article') {
                            next.file = null;
                          }
                          setMaterials((prev) => {
                            const copied = [...prev];
                            copied[idx] = { ...copied[idx], ...next };
                            return copied;
                          });
                        }}
                      >
                        <MenuItem value="記事">記事</MenuItem>
                        <MenuItem value="写真">写真</MenuItem>
                        <MenuItem value="ＥＰＳ">ＥＰＳ</MenuItem>
                        <MenuItem value="ＰＤＦ">ＰＤＦ</MenuItem>
                        <MenuItem value="エクセル">エクセル</MenuItem>
                        <MenuItem value="動画">動画</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box>
                    <TextField
                      label="登録予定日"
                      type="date"
                      fullWidth
                      size="small"
                      value={m.regDate || ''}
                      onChange={(e) => updateMaterial(idx, 'regDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box>
                    <TextField
                      label="行数"
                      fullWidth
                      size="small"
                      margin="dense"
                      variant="outlined"
                      type="number"
                      inputProps={{ min: 0 }}
                      InputLabelProps={{ shrink: true }}
                      value={m.lines ?? ''}
                      onChange={(e) => updateMaterial(idx, 'lines', sanitizeLineCount(e.target.value))}
                    />
                  </Box>
                </Box>

                {/* 2行目：仮見出し */}
                <Box sx={{ mb: 1 }}>
                  <TextField
                    label="仮見出し"
                    fullWidth
                    size="small"
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={m.headline}
                    onChange={(e) => updateMaterial(idx, 'headline', e.target.value)}
                  />
                </Box>

                {/* 3行目：関連URL */}
                <Box sx={{ mb: 1 }}>
                  <TextField
                    label="関連URL"
                    fullWidth
                    size="small"
                    margin="dense"
                    variant="outlined"
                    type="url"
                    InputLabelProps={{ shrink: true }}
                    value={m.linkUrl ?? ''}
                    onChange={(e) => updateMaterial(idx, 'linkUrl', e.target.value)}
                  />
                </Box>

                {showAttachSection && (
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      label="キャプション"
                      fullWidth
                      size="small"
                      margin="dense"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={m.caption ?? ''}
                      onChange={(e) => updateMaterial(idx, 'caption', e.target.value)}
                    />
                  </Box>
                )}

                {/* 4行目：編注（テキストエリア） */}
                <Box sx={{ mb: 1 }}>
                  <TextField
                    label="編注"
                    fullWidth
                    size="small"
                    margin="dense"
                    variant="outlined"
                    multiline
                    minRows={1}
                    value={m.note ?? ''}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => updateMaterial(idx, 'note', e.target.value)}
                  />
                </Box>

                {materialSlug === 'article' && (
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      label="記事本文"
                      fullWidth
                      size="small"
                      margin="dense"
                      variant="outlined"
                      multiline
                      minRows={4}
                      value={m.articleBody ?? ''}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => updateMaterial(idx, 'articleBody', e.target.value)}
                    />
                  </Box>
                )}

                {showAttachSection && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, mt: 1 }}>
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        sx={{ flexShrink: 0 }}
                      >
                        ファイル添付
                        <input
                          type="file"
                          hidden
                          accept={MATERIAL_FILE_ACCEPT_BY_SLUG[materialSlug] || undefined}
                          onChange={(e) => {
                            const selectedFile = e.target.files && e.target.files[0];
                            e.target.value = '';
                            if (!selectedFile) return;
                            const errorMessage = validateMaterialFile(materialSlug, selectedFile);
                            if (errorMessage) {
                              alert(errorMessage);
                              return;
                            }
                            updateMaterial(idx, 'file', selectedFile);
                          }}
                        />
                      </Button>

                      {m.file ? (
                        <>
                          <Typography
                            variant="caption"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {m.file.name}（{m.file.size >= 1024 * 1024
                              ? `${(m.file.size / (1024 * 1024)).toFixed(1)}MB`
                              : `${Math.max(1, Math.round(m.file.size / 1024))}KB`}）
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateMaterial(idx, 'file', null)}
                            aria-label="clear-file"
                            sx={{ flexShrink: 0 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          未添付
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      ※ 写真はJPEG形式（.jpg / .jpeg）・10MBまで
                    </Typography>
                  </>
                )}
                    </>
                  );
                })()}
              </Box>
            ))}

            <Box sx={{ textAlign: 'left', mt: 1 }}>
              <Button startIcon={<AddIcon />} onClick={addMaterial} size="small" variant="outlined">
                素材を追加
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          px: isMobile ? 1 : 3,
          py: isMobile ? 0.5 : 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            boxSizing: 'border-box',
            maxWidth: '100%',
            gap: isMobile ? 1 : 2,
            justifyContent: 'center',
            margin: isMobile ? '8px 0' : '15px 0',
          }}
        >
          {mode === 'schedule' && (
            <>
              <Button
                onClick={hasId ? handleDelete : () => calendarState.setOpen(false)}
                variant="contained"
                color="secondary"
                disabled={!hasId || isReadOnly}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  minWidth: isMobile ? 56 : 70,
                  px: isMobile ? 0.5 : 2,
                  py: isMobile ? 0.5 : 1,
                  fontSize: isMobile ? '0.75rem' : '1rem',
                  opacity: (hasId && !isReadOnly) ? 1 : 0.5,
                }}
              >
                <DeleteIcon sx={{ fontSize: isMobile ? 18 : 24 }} />
                {isMobile && <span style={{ fontSize: '0.7rem', marginTop: 2 }}>削除</span>}
                {!isMobile && '削除'}
              </Button>

              <Button
                onClick={openMemoMode}
                variant="contained"
                color="success"
                disabled={!hasId || isPrivateCategory || isReadOnly}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  minWidth: isMobile ? 56 : 70,
                  px: isMobile ? 0.5 : 2,
                  py: isMobile ? 0.5 : 1,
                  fontSize: isMobile ? '0.75rem' : '1rem',
                  opacity: (!hasId || isPrivateCategory || isReadOnly) ? 0.5 : 1,
                }}
              >
                <SendIcon sx={{ fontSize: isMobile ? 18 : 24 }} />
                {isMobile && <span style={{ fontSize: '0.7rem', marginTop: 2 }}>メモ</span>}
                {!isMobile && 'メモ'}
              </Button>

              <Button
                onClick={handleDuplicate}
                variant="contained"
                color="primary"
                disabled={!hasId || isReadOnly}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  minWidth: isMobile ? 56 : 70,
                  px: isMobile ? 0.5 : 2,
                  py: isMobile ? 0.5 : 1,
                  fontSize: isMobile ? '0.75rem' : '1rem',
                  opacity: (hasId && !isReadOnly) ? 1 : 0.5,
                }}
              >
                <ContentCopyIcon sx={{ fontSize: isMobile ? 18 : 24 }} />
                {isMobile && <span style={{ fontSize: '0.7rem', marginTop: 2 }}>複製</span>}
                {!isMobile && '複製'}
              </Button>

              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={isReadOnly}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  minWidth: isMobile ? 56 : 70,
                  px: isMobile ? 0.5 : 2,
                  py: isMobile ? 0.5 : 1,
                  fontSize: isMobile ? '0.75rem' : '1rem',
                }}
              >
                <SaveIcon sx={{ fontSize: isMobile ? 18 : 24 }} />
                {isMobile && <span style={{ fontSize: '0.7rem', marginTop: 2 }}>保存</span>}
                {!isMobile && '保存'}
              </Button>
            </>
          )}

          {mode === 'memo' && (
            isStandaloneMemoDialog ? (
              <>
                <Button
                  onClick={closeStandaloneMemoDialog}
                  variant="outlined"
                  sx={{
                    color: '#1a73e8',
                    borderColor: '#1a73e8',
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      borderColor: '#1765cc',
                      color: '#1765cc',
                      backgroundColor: '#f8fbff',
                    },
                  }}
                >
                  キャンセル
                </Button>

                <Button
                  onClick={handleStandalonePublish}
                  variant="contained"
                  color="success"
                  disabled={materials.length === 0 || isReadOnly}
                >
                  出稿
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setMode('schedule')}
                  variant="outlined"
                  color="primary"
                >
                  戻る
                </Button>

                <Button
                  onClick={publishMemoListXml}
                  variant="contained"
                  color="success"
                  disabled={materials.length === 0 || isReadOnly}
                >
                  メモ出稿
                </Button>
              </>
            )
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default EventDialog;
